import { mergeProps, PopoverPrimitive, ToggleTip, type PopoverPrimitiveOptions } from '@figma/fpl-components';
import { borderRadius, color as token, shadow } from '@figma/fpl-tokens/tokens.stylex';
import { ColorTheme } from '@figma/fpl-themes';
import MuiTooltip, { type TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';
import * as stylex from '@stylexjs/stylex';
import {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from 'react';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { mergeRefs, setForwardedRef } from '@/utils/refs';

type TriggerElementProps = Record<string, unknown> & { ref?: Ref<HTMLElement> };
type TriggerElement = ReactElement<TriggerElementProps> & { ref?: Ref<HTMLElement> };

export type TooltipProps = Omit<MuiTooltipProps, 'children'> & { children: TriggerElement };

function normalizePlacement(placement: MuiTooltipProps['placement']): PopoverPrimitiveOptions['placement'] {
  if (placement === 'auto') return 'bottom';
  if (placement === 'auto-start') return 'bottom-start';
  if (placement === 'auto-end') return 'bottom-end';
  return placement ?? 'bottom';
}

function hasTooltipTitle(title: ReactNode): boolean {
  return title === 0 || Boolean(title);
}

function useMergedTooltipTrigger(
  child: TriggerElement,
  triggerProps: TriggerElementProps,
  interactionProps?: TriggerElementProps,
): ReactElement {
  const childProps = child.props;
  const childRef = child.ref;
  const positioningRef = triggerProps.ref;
  const composedRef = useMemo(() => mergeRefs(childRef, positioningRef), [childRef, positioningRef]);
  const mergedProps = mergeProps(childProps, triggerProps, interactionProps);

  return cloneElement(child, { ...mergedProps, ref: composedRef });
}

function isTextContent(title: ReactNode): title is string | number {
  return typeof title === 'string' || typeof title === 'number';
}

const LEGACY_TOOLTIP_MAX_WIDTH = 300;

function TooltipContent({
  title,
  maxWidth = LEGACY_TOOLTIP_MAX_WIDTH,
}: {
  title: ReactNode;
  maxWidth?: string | number;
}) {
  if (!isTextContent(title)) return title;

  return <ToggleTip.Content maxWidth={maxWidth}>{title}</ToggleTip.Content>;
}

const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 12;
const ARROW_INSET = 1;
const TOOLTIP_OFFSET = 8 + ARROW_HEIGHT;

const styles = stylex.create({
  controlledTooltip: {
    ...stylex.env.mixin.fpl.typography.textBodyMedium,
    display: 'flex',
    borderRadius: borderRadius.medium,
    boxShadow: shadow.elevation300,
    backgroundColor: token.bgTooltip,
    color: token.textTooltip,
  },
  bottomArrow: {
    zIndex: -1,
    filter: 'drop-shadow(0 3px 5px rgb(0 0 0 / 35%)) drop-shadow(0 10px 24px rgb(0 0 0 / 45%))',
    stroke: 'rgb(255 255 255 / 15%)',
  },
});

function UncontrolledFplTooltip({
  children,
  disableHoverListener,
  onClose,
  placement,
  title,
}: Pick<TooltipProps, 'children' | 'disableHoverListener' | 'onClose' | 'placement' | 'title'>) {
  const manager = ToggleTip.useUncontrolledToggleTip({
    allowScrimPassthrough: true,
    offset: TOOLTIP_OFFSET,
    placement: normalizePlacement(placement),
    provideOwnClick: true,
  });
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !manager.isOpen) onClose?.({} as SyntheticEvent);
    wasOpenRef.current = manager.isOpen;
  }, [manager.isOpen, onClose]);

  // Closing at one instant isn't enough: an open can still be pending, and it then lands mid-drag and
  // aborts the drop. Stay suppressed until the gesture ends.
  const [isNativeDragging, setIsNativeDragging] = useState(false);

  const disabled = disableHoverListener || !hasTooltipTitle(title);
  const mergedTrigger = useMergedTooltipTrigger(children, disabled ? {} : manager.getTriggerProps(), {
    onDragStart: (event) => {
      // A drag the trigger cancels fires no dragend, so it must not leave the tooltip suppressed.
      if (event.defaultPrevented) return;
      // FPL blocks external pointer targets while a hover tooltip is open, so the canvas never
      // receives dragover or drop.
      setIsNativeDragging(true);
      if (manager.isOpen) manager.toggle();
    },
    onDragEnd: () => {
      setIsNativeDragging(false);
      if (manager.isOpen) manager.toggle();
    },
  });

  return (
    <>
      {mergedTrigger}
      {!disabled && !isNativeDragging && (
        <ToggleTip.Container manager={manager} role="tooltip" data-weavy-tooltip-pass-through="">
          <TooltipContent title={title} />
        </ToggleTip.Container>
      )}
    </>
  );
}

/**
 * MUI's `followCursor` for the FPL surface. The trigger stays the popover's element, for hover and dismiss,
 * while its position comes from a zero-size box at the pointer, which the per-frame update keeps following.
 */
function useCursorFollowingReference(setReference: Ref<HTMLElement> | undefined, enabled: boolean) {
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const ref = useCallback(
    (node: HTMLElement | null) => {
      setForwardedRef(setReference, node);
      if (!node) return;
      const pointerBox = {
        contextElement: node,
        getBoundingClientRect: () =>
          pointerRef.current
            ? new DOMRect(pointerRef.current.x, pointerRef.current.y, 0, 0)
            : node.getBoundingClientRect(),
      };
      // A non-element only replaces the positioning reference, the way Floating UI's `setPositionReference` does.
      setForwardedRef(setReference as Ref<unknown>, pointerBox);
    },
    [setReference],
  );
  if (!enabled) return undefined;
  return {
    ref,
    props: {
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        pointerRef.current = { x: event.clientX, y: event.clientY };
      },
    },
  };
}

function ControlledTooltipSurface({
  children,
  disableHoverListener,
  followCursor,
  leaveDelay = 0,
  onClose,
  onOpen,
  open,
  placement,
  slotProps,
  title,
}: Pick<
  TooltipProps,
  | 'children'
  | 'disableHoverListener'
  | 'followCursor'
  | 'leaveDelay'
  | 'onClose'
  | 'onOpen'
  | 'open'
  | 'placement'
  | 'slotProps'
  | 'title'
>) {
  const leaveTimerRef = useRef<number | undefined>(undefined);
  const lastRequestedOpenRef = useRef<boolean | undefined>(undefined);
  const previousOpenRef = useRef(open);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current !== undefined) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = undefined;
    }
  }, []);

  useEffect(() => clearLeaveTimer, [clearLeaveTimer, title, disableHoverListener, leaveDelay]);

  const requestOpenChange = useCallback(
    (nextOpen: boolean, event?: Event | SyntheticEvent) => {
      if (previousOpenRef.current !== open) {
        previousOpenRef.current = open;
        lastRequestedOpenRef.current = undefined;
      }
      if (nextOpen === open || lastRequestedOpenRef.current === nextOpen) return;
      lastRequestedOpenRef.current = nextOpen;
      if (nextOpen) onOpen?.(event as SyntheticEvent);
      else onClose?.(event as SyntheticEvent);
    },
    [onClose, onOpen, open],
  );

  const requestHoverClose = useCallback(
    (event?: Event | SyntheticEvent) => {
      clearLeaveTimer();
      if (open !== true) return;
      if (leaveDelay > 0) {
        leaveTimerRef.current = window.setTimeout(() => requestOpenChange(false, event), leaveDelay);
      } else {
        requestOpenChange(false, event);
      }
    },
    [clearLeaveTimer, leaveDelay, open, requestOpenChange],
  );

  const { getArrowProps, getContainerProps, getTriggerProps } = PopoverPrimitive.usePopover<HTMLElement>({
    allowScrimPassthrough: true,
    offset: TOOLTIP_OFFSET,
    type: 'tooltip',
    isOpen: open === true,
    onOpenChange: (nextOpen, event, reason) => {
      if (!nextOpen && (reason === 'hover' || reason === 'safe-polygon')) {
        requestHoverClose(event);
      } else {
        clearLeaveTimer();
        requestOpenChange(nextOpen, event);
      }
    },
    openOnHover: !disableHoverListener,
    placement: normalizePlacement(placement),
    arrowPadding: 5,
    provideOwnClick: true,
  });

  const hoverProps = disableHoverListener
    ? undefined
    : {
        onMouseEnter: () => {
          clearLeaveTimer();
          if (open !== true) requestOpenChange(true);
        },
      };
  const triggerProps = getTriggerProps();
  const follow = useCursorFollowingReference(triggerProps.ref, followCursor === true);
  const mergedTrigger = useMergedTooltipTrigger(
    children,
    follow ? { ...triggerProps, ref: follow.ref } : triggerProps,
    { ...hoverProps, ...follow?.props },
  );
  const maxWidth = (slotProps?.tooltip as { sx?: { maxWidth?: string | number } } | undefined)?.sx?.maxWidth;
  const containerProps = mergeProps(
    getContainerProps({
      role: 'tooltip',
      ...stylex.props(styles.controlledTooltip),
    }),
    maxWidth === undefined ? undefined : { style: { maxWidth } },
    disableHoverListener ? undefined : { onMouseEnter: clearLeaveTimer },
  );
  const translate = { x: 0, y: -1 };

  return (
    <>
      {mergedTrigger}
      {/* Pinned dark on purpose — do NOT switch this to the app theme mode. Tooltips are an
          inverse surface in both modes: `--color-bg-tooltip` is `grey-900` and
          `--color-text-tooltip` is white in the FPL light and dark blocks alike. Inheriting the app
          mode would flip the tooltip's non-tooltip tokens to light over that dark surface. */}
      <ColorTheme mode="dark">
        <PopoverPrimitive.Container {...containerProps} data-weavy-tooltip-pass-through="">
          <PopoverPrimitive.Arrow
            {...getArrowProps()}
            width={ARROW_WIDTH + 2 * ARROW_INSET}
            height={ARROW_HEIGHT + ARROW_INSET}
            fill={token.bgTooltip}
            {...stylex.props(styles.bottomArrow)}
            translate={translate}
          />
          <PopoverPrimitive.Arrow
            {...getArrowProps()}
            width={ARROW_WIDTH + 2 * ARROW_INSET}
            height={ARROW_HEIGHT + ARROW_INSET}
            fill={token.bgTooltip}
            translate={translate}
          />
          <TooltipContent title={title} maxWidth={maxWidth} />
        </PopoverPrimitive.Container>
      </ColorTheme>
    </>
  );
}

/**
 * Weavy's Tooltip compatibility boundary. The legacy MUI implementation remains unchanged with
 * `fpl-migration` off; flag-on tooltips use FPL ToggleTip or a controlled PopoverPrimitive surface.
 */
export function Tooltip(props: TooltipProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled !== true) return <MuiTooltip {...props} />;
  if (props.open !== undefined) {
    if (!hasTooltipTitle(props.title)) return props.children;
    return <ControlledTooltipSurface {...props} />;
  }
  return <UncontrolledFplTooltip {...props} />;
}
